import { redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { cached } from "@/lib/ssrCache";

/**
 * Product pages that were unpublished during the thin-content prune are
 * permanently redirected (301) to their hub page so link equity is preserved
 * and crawlers stop hitting soft 404s.
 *
 * The pruned slug list per hub is cached in the server isolate so product
 * pages don't pay for a database round-trip on every render (this was adding
 * noticeable TTFB for crawlers).
 */
async function loadPrunedSlugs(hubSlug: string): Promise<Set<string>> {
  const slugs = new Set<string>();
  const batchSize = 1000;
  let from = 0;
  while (true) {
    const { data, error } = await supabase
      .from("product_prune_log")
      .select("slug")
      .eq("product_type", hubSlug)
      .range(from, from + batchSize - 1);
    if (error || !data || data.length === 0) break;
    data.forEach((row: { slug: string | null }) => {
      if (row.slug) slugs.add(row.slug);
    });
    if (data.length < batchSize) break;
    from += batchSize;
  }
  return slugs;
}

export async function redirectIfPruned(hubSlug: string, productSlug: string) {
  if (!hubSlug || !productSlug) return;

  let pruned: Set<string>;
  try {
    pruned = await cached(
      `pruned:${hubSlug}`,
      30 * 60 * 1000,
      () => loadPrunedSlugs(hubSlug),
    );
  } catch {
    // Network/query failure: render the page normally rather than misdirecting.
    return;
  }

  if (pruned.has(productSlug)) {
    throw redirect({ href: `/${hubSlug}`, statusCode: 301 });
  }
}
